import { useState, useRef } from 'react';
import { Button, IconButton, MixerLoader, PremiumTooltip } from '../../components/Common';
import { useTranslation } from '../../hooks/useTranslation';
import { useHttpUpdateProjectStatus } from '../../hooks';
import { useHttpGetAllProjectsWithAssignees } from '../../hooks/acs/acsApi';
import { ProjectDtoRelV2 } from '../../commonTypes/project.types';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuth } from '../../store/slices/auth.slice';
import { updateCachedProject } from '../../store/slices/projects.slice';
import { RoleTypeEnum } from '../../commonTypes/user.types';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { ExpandableSearch } from '../../components/Common/ExpandableSearch';
import { Formik } from 'formik';
import { ProjectsTable } from './TableView';
import { ProjectsGridView } from './GridView';
import { ProjectsCalendarView } from './CalendarView';
import { ViewToggle } from '../../components/Projects';
import { ProjectStatus, Feature } from '@cyberlabrs/helpers';
import { useFeaturePlanLimits } from '../../hooks/features';
import cc from 'classcat';
import NotAvailableOnMobile from '../../components/Common/NotAvailableOnMobile';
import { RootState } from '../../store';
import { setSearchTerm, setSelectedFilters, setView } from '../../store/slices/projectsUi.slice';
import { ViewToggleType } from '../../components/Projects/ViewToggle';

interface IMenuItem {
    label: string;
    value: string;
}

const NoProjects = () => {
    const { t } = useTranslation('projects');
    const navigate = useNavigate();

    const authUser = useSelector(selectAuth);
    const isAuth =
        authUser.role.name !== RoleTypeEnum.ORDINARY && authUser.role.name !== RoleTypeEnum.BASIC && authUser.role.name !== RoleTypeEnum.TEAM_LEAD;

    const { isLimitReached } = useFeaturePlanLimits(Feature.PROJECTS, 'create', []);

    return (
        <main className="no-project">
            <div>
                <img src="/svg/no-projects-2.svg" alt="No projects" className="no-projects__image" />

                <div className="no-project__content">
                    {isAuth ? (
                        <>
                            <h3>{t('noProjectYet')}</h3>
                            <p>{t('noProjectYetDescription')}</p>

                            <div className={cc(['premium-tooltip-wrapper', isLimitReached && 'projects__add-button-wrapper'])}>
                                <Button
                                    text={t('addNewProject')}
                                    onClick={() => !isLimitReached && navigate('/create-project')}
                                    disabled={isLimitReached}
                                />
                                {isLimitReached && <PremiumTooltip position="bottom" showOnHover />}
                            </div>
                        </>
                    ) : (
                        <>
                            <h3>{t('noProjectAssigned')}</h3>
                            <p>{t('noProjectAssignedDescription')}</p>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};

interface FormValues {
    search: string;
}

interface ProjectsProps {
    projects: ProjectDtoRelV2[];
    setProjects?: React.Dispatch<React.SetStateAction<ProjectDtoRelV2[]>>;
}

const Projects = ({ projects, setProjects }: ProjectsProps) => {
    const { searchTerm, selectedFilters, view } = useSelector((state: RootState) => state.projectsUi);
    const { isXSmall, isSmall } = useResizeListener();
    const isSmallScreen = isSmall || isXSmall;
    const { t } = useTranslation('projects');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [tempFilters, setTempFilters] = useState(selectedFilters);
    const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);

    const filtersRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLImageElement>(null);

    const authUser = useSelector(selectAuth);
    const isAuth = ![RoleTypeEnum.ORDINARY, RoleTypeEnum.BASIC, RoleTypeEnum.TEAM_LEAD].includes(authUser.role.name);

    const { exec: updateProjectStatus } = useHttpUpdateProjectStatus();
    const { isLimitReached } = useFeaturePlanLimits(Feature.PROJECTS, 'create', projects);

    const menuItems: IMenuItem[] = [
        { label: 'In Progress', value: 'InProgress' },
        { label: 'Completed', value: 'Completed' },
        { label: 'On Hold', value: 'OnHold' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Kick Off', value: 'KickOff' },
    ];

    const handleCheckboxChange = (value: string) => {
        setTempFilters(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
    };
    const handleViewChange = (newView: ViewToggleType) => {
        dispatch(setView(newView));
    };

    const handleApplyFilters = () => {
        dispatch(setSelectedFilters(tempFilters));
        setAreFiltersExpanded(false);
    };

    const handleResetFilters = () => {
        dispatch(setSelectedFilters([]));
        setTempFilters([]);
        setAreFiltersExpanded(false);
    };

    const handleUpdateProjectStatus = (projectId: string, newStatus: string) => {
        updateProjectStatus(projectId, newStatus as ProjectStatus, () => {
            if (setProjects) {
                setProjects(prevProjects =>
                    prevProjects.map(project => {
                        if (project.id === projectId) {
                            const updatedProject = { ...project, projectStatus: newStatus as ProjectStatus };
                            dispatch(updateCachedProject(updatedProject));
                            return updatedProject;
                        }
                        return project;
                    })
                );
            }
        });
    };

    return (
        <Formik<FormValues> enableReinitialize initialValues={{ search: searchTerm }} onSubmit={() => {}}>
            {formik => (
                <main className="entity-layout">
                    <div>
                        {isAuth && (
                            <div className="projects__header">
                                <ExpandableSearch
                                    id="search"
                                    placeholder="Search..."
                                    context={formik}
                                    className="invoices__search"
                                    isCloseButtonVisible={formik.values.search !== ''}
                                    setSearchTerm={value => {
                                        formik.setFieldValue('search', value);
                                        dispatch(setSearchTerm(value));
                                    }}
                                    closeButtonOnClick={() => {
                                        formik.setFieldValue('search', '');
                                        dispatch(setSearchTerm(''));
                                    }}
                                />

                                <div className="projects__header-container">
                                    <img
                                        ref={toggleRef}
                                        src={areFiltersExpanded ? '/svg/common/filters-blue.svg' : '/svg/common/filters.svg'}
                                        alt="Filter"
                                        onClick={() => setAreFiltersExpanded(prev => !prev)}
                                    />
                                    {!isLimitReached && <ViewToggle value={view} onChange={handleViewChange} />}
                                    {areFiltersExpanded && (
                                        <div ref={filtersRef} className="invoices__actions__filters">
                                            <p className="invoices__actions__filters__title">{'Status'}</p>
                                            <div className="invoices__actions__filters__options">
                                                {menuItems.map(item => (
                                                    <label key={item.value} className="invoices__actions__filters__checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={tempFilters.includes(item.value as string)}
                                                            onChange={() => handleCheckboxChange(item.value as string)}
                                                        />
                                                        <span>{item.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="invoices__actions__filters__options__buttons">
                                                <Button text={'Apply Filters'} onClick={handleApplyFilters} />
                                                <Button text={'Reset Filters'} variant="text" onClick={handleResetFilters} color="primary" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {isSmallScreen ? (
                                    <div className={cc(['premium-tooltip-wrapper', isLimitReached && 'projects__add-button-wrapper'])}>
                                        <IconButton
                                            onClick={() => !isLimitReached && navigate('/create-project')}
                                            shape="circle"
                                            iconUrl="/svg/plus.svg"
                                            iconAlt="create"
                                            disabled={isLimitReached}
                                        />
                                        {isLimitReached && <PremiumTooltip position="bottom" showOnHover />}
                                    </div>
                                ) : (
                                    <div className={cc(['premium-tooltip-wrapper', isLimitReached && 'projects__add-button-wrapper'])}>
                                        <Button
                                            text={t('addNewProject')}
                                            onClick={() => !isLimitReached && navigate('/create-project')}
                                            disabled={isLimitReached}
                                        />
                                        {isLimitReached && <PremiumTooltip position="bottom" showOnHover />}
                                    </div>
                                )}
                            </div>
                        )}
                        {view === 'list' || isLimitReached ? (
                            <ProjectsTable projects={projects} searchTerm={searchTerm} selectedFilters={selectedFilters} />
                        ) : view === 'module' ? (
                            <ProjectsGridView
                                projects={projects}
                                searchTerm={searchTerm}
                                selectedFilters={selectedFilters}
                                onUpdateProjectStatus={handleUpdateProjectStatus}
                            />
                        ) : (
                            <ProjectsCalendarView projects={projects} searchTerm={searchTerm} selectedFilters={selectedFilters} />
                        )}
                    </div>
                </main>
            )}
        </Formik>
    );
};

const ProjectsSafetyWrapper = () => {
    const navigate = useNavigate();
    const isMobile = useResizeListener().isMaxWidthLarge;

    const { isReady, projects, setProjects } = useHttpGetAllProjectsWithAssignees();
    const { t: mobileT } = useTranslation('notAvailableOnMobile');

    if (isMobile) {
        return (
            <NotAvailableOnMobile
                title={mobileT('title')}
                paragraph={mobileT('paragraph')}
                buttonText={mobileT('button')}
                onClick={() => navigate('/')}
            />
        );
    }

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (projects.length === 0) {
        return <NoProjects />;
    }

    return <Projects projects={projects} setProjects={setProjects} />;
};

export default ProjectsSafetyWrapper;
